import React, { useEffect, useState } from "react";
import { fetchPlans, createSubscription } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { RedeemCodePanel } from "./RedeemCodePanel";

/**
 * Big blocky player tier cards (matches the user-provided mockup).
 * Casual: cyan block + soccer-ball / icon
 * PRO:    purple/magenta block + crown + blue check
 */
export function PlayerProfilesSection({ onToast, onRequireAuth }) {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);

  useEffect(() => { fetchPlans().then(setPlans); }, []);

  const playerPlans = plans.filter((p) => p.audience === "player");
  const isPro = user?.user_type === "pro" || user?.plan === "pro";

  const handleUpgrade = async () => {
    if (!user) { onRequireAuth(); return; }
    try {
      // Real Stripe Subscriptions checkout — same flow as PricingSection.
      // `createSubscription` returns `{ url }` to redirect to Stripe.
      const r = await createSubscription("pro_player", window.location.origin);
      if (r?.url) {
        window.location.href = r.url;
      } else {
        onToast?.("Couldn't start checkout — retry.");
      }
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Upgrade failed.");
    }
  };

  const casual = playerPlans.find((p) => p.plan_id === "player-free");
  const pro = playerPlans.find((p) => p.plan_id === "player-pro");

  return (
    <section
      id="player-tiers"
      className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-16 page-z"
      data-testid="player-profiles-section"
    >
      <div className="text-center mb-10">
        <span className="px-4 py-1.5 text-[10px] font-bold tracking-[3px] bg-[#1e2a3a]/15 text-[#1e2a3a] rounded-full">
          YOUR NEON IDENTITY
        </span>
        <h2 className="text-3xl sm:text-5xl md:text-6xl font-display grad-text mt-5">PLAYER PROFILES</h2>
        <p className="text-base text-[#a3a3ac] mt-3">FREE Casual • $1.99/mo PRO Player</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
        {/* Casual Block (cyan) */}
        <div
          data-testid="casual-player-block"
          className="card-3d rounded-[28px] p-10 md:p-12 relative overflow-hidden text-center min-h-[420px] flex flex-col items-center justify-center"
          style={{
            background: "linear-gradient(180deg, #2cd9ff 0%, #00b8ff 100%)",
            boxShadow: "0 0 36px rgba(0,184,255,0.5), inset 0 -6px 0 rgba(0,0,0,0.15)",
          }}
        >
          <div className="text-9xl md:text-[140px] leading-none mb-6 drop-shadow-2xl">⚽</div>
          <h3 className="text-3xl md:text-4xl font-display text-black tracking-[3px]">CASUAL PLAYER</h3>
          <p className="text-4xl md:text-5xl font-display text-black mt-3 tracking-[6px]">FREE</p>
          <ul className="mt-6 text-sm text-black/85 space-y-1.5 font-bold text-left max-w-xs">
            <li>✓ Gamer name + 12 preset avatars</li>
            <li>✓ Link Discord + Google</li>
            <li>✓ Get invited to any league</li>
            <li>✓ Join up to 10-player squads</li>
          </ul>
          <button
            onClick={() => !user ? onRequireAuth() : onToast("You're already in the free tier. Drop in!")}
            data-testid="casual-player-cta"
            className="mt-7 px-8 py-3 rounded-2xl bg-black text-white font-display tracking-[3px] text-sm hover:scale-[1.03] transition-transform"
          >
            {isPro ? "DOWNGRADE" : (user ? "ACTIVE" : "START FREE")} →
          </button>
        </div>

        {/* PRO Block (magenta/purple) */}
        <div
          data-testid="pro-player-block"
          className="card-3d rounded-[28px] p-10 md:p-12 relative overflow-hidden text-center min-h-[420px] flex flex-col items-center justify-center"
          style={{
            background: "linear-gradient(180deg, #d042ff 0%, #1e2a3a 50%, #14b8a6 100%)",
            boxShadow: "0 0 44px rgba(20, 184, 166,0.7), inset 0 -6px 0 rgba(0,0,0,0.2)",
          }}
        >
          {/* Crown badge */}
          <div className="absolute top-5 right-5 text-3xl md:text-4xl drop-shadow-lg">👑</div>
          {/* Avatar circle with blue check */}
          <div className="relative mb-6">
            <div
              className="w-32 h-32 md:w-36 md:h-36 rounded-full overflow-hidden border-4 border-white/80"
              style={{
                background: user?.pfp_url
                  ? `url(${user.pfp_url}) center/cover`
                  : "linear-gradient(135deg, #fdba74, #fb923c, #ea580c)",
                boxShadow: "0 0 24px rgba(0,0,0,0.3)",
              }}
            >
              {!user?.pfp_url && (
                <div className="w-full h-full flex items-center justify-center text-6xl md:text-7xl">
                  🧑‍🚀
                </div>
              )}
            </div>
            {/* Blue check */}
            <div
              className="absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-[#1d9bf0] border-4 border-white flex items-center justify-center"
              style={{ boxShadow: "0 0 12px rgba(29,155,240,0.7)" }}
              data-testid="pro-blue-check"
            >
              <i className="fas fa-check text-white text-sm" />
            </div>
          </div>
          <h3 className="text-3xl md:text-4xl font-display text-white tracking-[3px]">PRO PLAYER</h3>
          <p className="text-4xl md:text-5xl font-display text-white mt-3 tracking-[3px]" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.3)" }}>
            $1.99<span className="text-lg align-super opacity-80">/mo</span>
          </p>
          <ul className="mt-6 text-sm text-white space-y-1.5 font-bold text-left max-w-xs">
            <li>✓ Custom photo upload + AI avatar</li>
            <li>✓ Blue check + glowing PRO badge</li>
            <li>✓ Priority on LFG &amp; matchmaking</li>
            <li>✓ Unlimited league applications</li>
          </ul>
          {isPro ? (
            <button
              disabled
              data-testid="pro-player-active"
              className="mt-7 px-8 py-3 rounded-2xl bg-black/40 text-white font-display tracking-[3px] text-sm"
            >
              ACTIVE PRO ✓
            </button>
          ) : (
            <button
              onClick={handleUpgrade}
              data-testid="pro-player-cta"
              className="mt-7 px-8 py-3 rounded-2xl bg-black text-white font-display tracking-[3px] text-sm hover:scale-[1.03] transition-transform relative"
            >
              ACTIVATE PRO → 🔥
              <span className="absolute -top-2 right-2 text-[9px] tracking-widest bg-[#0a0a1f] text-[#14b8a6] px-2 py-0.5 rounded-full border border-[#14b8a6]/40">
                STRIPE
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Pay-free PRO unlock — same component used on /pricing.
          Sits below the tier cards so the redeem path is one
          discoverable surface from the profile page too. */}
      {!isPro && (
        <div className="mt-10">
          <RedeemCodePanel onToast={onToast} onRequireAuth={onRequireAuth} compact />
        </div>
      )}
    </section>
  );
}

export default PlayerProfilesSection;
